import { AppInsightsLogger } from "modules/logging/appInsightsLogger";
import { GoogleAnalyticsLogger } from "modules/logging/googleAnalyticsLogger";

import * as AuthActions from "modules/auth/actions";
import * as BenefitCoverageActions from "modules/benefits/coverage/actions";
import * as DrugSearchActions from "modules/benefits/drugs/actions";
import * as MembershipCardActions from "modules/benefits/membershipCard/actions";
import * as CommonActions from "modules/common/actions";
import * as DocumentActions from "modules/documents/actions";
import * as FeatureActions from "modules/features/actions";
import * as ProfileAddressActions from "modules/profile/address/actions";
import * as ProfileBankAccountActions from "modules/profile/bankAccount/actions";
import * as ProfilePhoneNumberActions from "modules/profile/phoneNumbers/actions";
import * as RegistrationActions from "modules/profile/registration/actions";
import * as ProfileVerificationActions from "modules/profile/verification/actions";
import * as ServiceProvidersActions from "modules/serviceProviders/actions";
import * as ContactActions from "modules/contact/actions";
import * as TaxSlipsPreferenceActions from "modules/preferences/taxSlips/actions";
import * as InboxActions from "modules/inbox/actions";
import * as DentalGuideActions from "modules/benefits/dentalGuide/actions";

import { Formatter, getSecondsSinceDate } from "modules/utils";
import FeatureToggleChecker from "modules/utils/featureToggleChecker";
import { UserActivityKeys } from "../features/models";

type LoggedAction =
    | AuthActions.LogInCompleteAction
    | AuthActions.LogInFirstStage2faAction
    | AuthActions.VerifyOtpCompleteAction
    | AuthActions.ReauthenticateCompleteAction
    | AuthActions.SetUserIdleStatus
    | BenefitCoverageActions.SaveOverAgeDependantDeclarationActionComplete
    | CommonActions.TrackMobileNavDrawerOpenedAction
    | CommonActions.TrackLoggedInUserIsEmulatedAction
    | DocumentActions.TrackDocumentDownloadAction
    | DrugSearchActions.SearchDrugsComplete
    | DrugSearchActions.GetDrugDetailsComplete
    | FeatureActions.SaveUserActivityCompleteAction
    | MembershipCardActions.ShowMembershipCard
    | MembershipCardActions.HideMembershipCard
    | MembershipCardActions.DownloadMembershipCard
    | ProfilePhoneNumberActions.SavePhoneNumbersCompleteAction
    | ProfileBankAccountActions.SaveBankAccountCompleteAction
    | ProfileBankAccountActions.RemoveBankAccountCompleteAction
    | ProfileAddressActions.SaveAddressCompleteAction
    | ProfileVerificationActions.VerifyCompleteAction
    | RegistrationActions.InitializeNewMemberRegistrationAction
    | RegistrationActions.VerifyNewMemberCompleteAction
    | RegistrationActions.FinalizeMemberRegistrationCompleteAction
    | RegistrationActions.CancelNewMemberRegistrationAction
    | ServiceProvidersActions.SetSelectedProviderClassAction
    | ServiceProvidersActions.SaveServiceProviderRatingResponseCompleteAction
    | ServiceProvidersActions.SubmitServiceProviderRatingComplete
    | ServiceProvidersActions.GetIneligibleProvidersCompleteAction
    | ServiceProvidersActions.GetProviderLocationsCompleteAction
    | ServiceProvidersActions.GetHomeAddressGeoCoordinateCompleteAction
    | ContactActions.SubmitFeedbackCompleteAction
    | ContactActions.SubmitInquiryCompleteAction
    | ProfileVerificationActions.LogDeepLinkAction
    | TaxSlipsPreferenceActions.SavePreferenceCompleteAction
    | InboxActions.SubmitNewConversationCompleteAction
    | InboxActions.SubmitNewConversationMessageCompleteAction
    | InboxActions.CloseConversationCompleteAction
    | InboxActions.GetConversationsCompleteAction
    | InboxActions.GetConversationCompleteAction
    | InboxActions.ReOpenConversationCompleteAction
    | DentalGuideActions.GetDentalProcedureDetailCompleteAction;

function createLoggingMiddleware() {
    return (store) => (next) => (action: LoggedAction) => {
        switch (action.type) {
            case "REOPEN_CONVERSATION_COMPLETE": {
                const area = "Inbox";
                const event = "ConversationReOpened";
                const eventName = `FeatureUsage-${area}-${event}}`;
                AppInsightsLogger.trackEvent(
                    { name: eventName },
                    { name: event, area: area, action: action.type, succeeded: action.succeeded }
                );
                GoogleAnalyticsLogger.trackEvent(area, event, action.succeeded ? "succeeded" : "failed");
                break;
            }
            case "GET_CONVERSATION_COMPLETE": {
                const area = "Inbox";
                const event = "Get Conversation";
                const eventName = `${event}: ${action.succeeded ? "succeeded" : "failed"}`;
                AppInsightsLogger.trackEvent(
                    { name: event },
                    { name: eventName, area: area, action: action.type, succeeded: action.succeeded }
                );
                GoogleAnalyticsLogger.trackEvent(area, event, action.succeeded ? "succeeded" : "failed");
                break;
            }
            case "GET_CONVERSATIONS_COMPLETE": {
                const area = "Inbox";
                const event = "Get Conversations";
                const eventName = `${event}: ${action.succeeded ? "succeeded" : "failed"}`;
                AppInsightsLogger.trackEvent(
                    { name: event },
                    { name: eventName, area: area, action: action.type, succeeded: action.succeeded }
                );
                GoogleAnalyticsLogger.trackEvent(area, event, action.succeeded ? "succeeded" : "failed");
                break;
            }
            case "CLOSE_CONVERSATION_COMPLETE": {
                const area = "Inbox";
                const event = "ConversationResolved";
                const eventName = `FeatureUsage-${area}-${event}`;
                AppInsightsLogger.trackEvent(
                    { name: eventName },
                    { name: event, area: area, action: action.type, succeeded: action.succeeded }
                );
                GoogleAnalyticsLogger.trackEvent(area, event, action.succeeded ? "succeeded" : "failed");
                break;
            }
            case "SUBMIT_NEW_CONVERSATION_COMPLETE": {
                const area = "Inbox";
                const event = "NewConversationSubmitted";
                const eventName = `FeatureUsage-${area}-${event}`;
                AppInsightsLogger.trackEvent(
                    { name: eventName },
                    { name: event, area: area, action: action.type, succeeded: action.succeeded }
                );
                GoogleAnalyticsLogger.trackEvent(area, event, action.succeeded ? "succeeded" : "failed");
                break;
            }
            case "SUBMIT_NEW_CONVERSATION_MESSAGE_COMPLETE": {
                const area = "Inbox";
                const event = "NewConversationMessageSubmitted";
                const eventName = `FeatureUsage-${area}-${event}`;
                AppInsightsLogger.trackEvent(
                    { name: eventName },
                    { name: event, area: area, action: action.type, succeeded: action.succeeded }
                );
                GoogleAnalyticsLogger.trackEvent(area, event, action.succeeded ? "succeeded" : "failed");
                break;
            }
            case "INITIALIZE_NEW_MEMBER_REGISTRATION": {
                const eventName = "Registration started";
                AppInsightsLogger.trackEvent({ name: eventName });
                GoogleAnalyticsLogger.trackEvent("Registration", eventName);
                break;
            }
            case "VERIFY_NEW_MEMBER_COMPLETE": {
                if (action.succeeded) {
                    const eventName = "Registration: verification complete";

                    AppInsightsLogger.trackEvent({ name: eventName });
                    GoogleAnalyticsLogger.trackEvent("Registration", eventName);
                } else {
                    const eventName = "Registration: verification failed";

                    AppInsightsLogger.trackEvent({ name: eventName });
                    GoogleAnalyticsLogger.trackEvent("Registration", eventName);
                }
                break;
            }
            case "FINALIZE_MEMBER_REGISTRATION_COMPLETE": {
                if (action.succeeded) {
                    const eventName = "Registration: complete";

                    AppInsightsLogger.trackEvent({ name: eventName });
                    GoogleAnalyticsLogger.trackEvent("Registration", eventName);
                } else {
                    const eventName = "Registration: failed";
                    const isEmailAddressAvailable = (action.isEmailAddressAvailable || "false").toString();
                    const isPasswordStrongEnough = (action.isPasswordStrongEnough || "").toString();

                    AppInsightsLogger.trackEvent(
                        { name: eventName },
                        { isEmailAddressAvailable, isPasswordStrongEnough }
                    );
                    GoogleAnalyticsLogger.trackEvent("Registration", eventName);
                }
                break;
            }
            case "CANCEL_NEW_MEMBER_REGISTRATION": {
                const eventName = "Registration: cancelled";
                AppInsightsLogger.trackEvent({ name: eventName });
                GoogleAnalyticsLogger.trackEvent("Registration", eventName);
                break;
            }
            case "LOG_IN_FIRST_STAGE_2FA": {
                const area = "TwoFactor";
                const event = "FormShown";
                const eventName = `FeatureUsage-${area}-${event}`;
                AppInsightsLogger.trackEvent({ name: eventName }, { username: action.username });
                break;
            }
            case "VERIFY_OTP_COMPLETE": {
                if (action.failureReason === "invalidOrExpired") {
                    const area = "TwoFactor";
                    const event = "InvalidOrExpiredCode";
                    const eventName = `FeatureUsage-${area}-${event}`;
                    const username = store.getState()?.auth?.login?.username;
                    AppInsightsLogger.trackEvent({ name: eventName }, { username });
                }
                break;
            }
            case "LOG_IN_COMPLETE": {
                if (action.authResponse.succeeded) {
                    const eventName = "Logged in";
                    AppInsightsLogger.setAuthenticatedUserContext(action.authResponse.userId);
                    AppInsightsLogger.trackEvent({ name: eventName });
                    GoogleAnalyticsLogger.setUserId(action.authResponse.userId);
                    GoogleAnalyticsLogger.trackEvent("Auth", eventName);

                    // we want to log any instances of user logging in with username/password
                    // when 2FA feature is on
                    const twoFactorToggle = store.getState()?.features?.featureToggles?.Enable2FA;
                    if (!action.usedTwoFactor && FeatureToggleChecker.isOn(twoFactorToggle)) {
                        const area = "TwoFactor";
                        const event = "LoggedInWithoutTwoFactor";
                        const twoFactorEventName = `FeatureUsage-${area}-${event}`;
                        AppInsightsLogger.trackEvent({ name: twoFactorEventName }, { username: action.username });
                    }
                } else {
                    const eventName = "Log in failed";
                    const eventData = { message: action.authResponse.message, username: action.username };
                    AppInsightsLogger.trackEvent({ name: eventName }, eventData);
                    GoogleAnalyticsLogger.trackEvent("Auth", eventName, action.authResponse.message);
                }
                break;
            }
            case "REAUTHENTICATE_COMPLETE": {
                if (action.authResponse.succeeded) {
                    const eventName = "Reauthenticated";
                    AppInsightsLogger.trackEvent({ name: eventName });
                    GoogleAnalyticsLogger.trackEvent("Auth", eventName);
                } else {
                    const eventName = "Reauthentication failed";
                    AppInsightsLogger.trackEvent({ name: eventName }, { message: action.authResponse.message });
                    GoogleAnalyticsLogger.trackEvent("Auth", eventName, action.authResponse.message);
                }
                break;
            }
            case "SAVE_USER_ACTIVITY_COMPLETE": {
                switch (action.key) {
                    case UserActivityKeys.LastViewedFeatureArticles: {
                        const eventName = "Viewed feature article list";
                        AppInsightsLogger.trackEvent({ name: eventName });
                        GoogleAnalyticsLogger.trackEvent("Features", eventName);
                    }
                }
            }
            case "SHOW_MEMBERSHIP_CARD": {
                const eventName = "Viewed wallet card";
                AppInsightsLogger.trackEvent({ name: eventName });
                GoogleAnalyticsLogger.trackEvent("Wallet Card", eventName);
                GoogleAnalyticsLogger.trackModalView();
                break;
            }
            case "HIDE_MEMBERSHIP_CARD": {
                const eventName = "Closed wallet card";
                AppInsightsLogger.trackEvent({ name: eventName });
                GoogleAnalyticsLogger.trackEvent("Wallet Card", eventName);
                GoogleAnalyticsLogger.trackModalView();
                break;
            }
            case "DOWNLOAD_MEMBERSHIP_CARD": {
                const eventName = "Downloaded wallet Card";
                AppInsightsLogger.trackEvent({ name: eventName });
                GoogleAnalyticsLogger.trackEvent("Wallet Card", eventName);
                GoogleAnalyticsLogger.trackModalView();
                break;
            }
            case "SET_USER_IDLE": {
                if (action.idle != null && action.idle) {
                    const eventName = "Session timed out";
                    AppInsightsLogger.trackEvent({ name: eventName });
                    GoogleAnalyticsLogger.trackEvent("Auth", eventName);
                } else {
                    const eventName = "Session resumed";
                    AppInsightsLogger.trackEvent({ name: eventName });
                    GoogleAnalyticsLogger.trackEvent("Auth", eventName);
                }
                break;
            }
            case "SAVE_OVER_AGE_DEPENDANT_DECLARATION_COMPLETE": {
                if (action.succeeded) {
                    const eventName = "Over-age dependant declaration submitted";
                    const props = {
                        schoolYearId: action.declaration.schoolYearId.toString(),
                        coveredMemberCertificateNumber: action.declaration.coveredMemberCertificateNumber,
                        dependantCertificateNumber: action.declaration.dependantCertificateNumber
                    };
                    AppInsightsLogger.trackEvent({ name: eventName }, props);
                    GoogleAnalyticsLogger.trackEvent("Coverage", eventName);
                } else {
                    const eventName = "Over-age dependant declaration submission failed";
                    const props = {
                        schoolYearId: action.declaration.schoolYearId.toString(),
                        coveredMemberCertificateNumber: action.declaration.coveredMemberCertificateNumber,
                        dependantCertificateNumber: action.declaration.dependantCertificateNumber
                    };
                    AppInsightsLogger.trackEvent({ name: eventName }, props);
                    GoogleAnalyticsLogger.trackEvent("Coverage", eventName);
                }
                break;
            }
            case "SAVE_ADDRESS_COMPLETE": {
                if (action.succeeded) {
                    const eventName = "Profile: Address saved";
                    AppInsightsLogger.trackEvent({ name: eventName });
                    GoogleAnalyticsLogger.trackEvent("Profile", eventName);
                } else {
                    const eventName = "Profile: Address save failed";
                    AppInsightsLogger.trackEvent({ name: eventName });
                    GoogleAnalyticsLogger.trackEvent("Profile", eventName);
                }
                break;
            }
            case "SAVE_BANK_ACCOUNT_COMPLETE": {
                if (action.succeeded) {
                    const eventName = "Profile: Bank account saved";
                    const props = {
                        bankAccountType: action.bankAccount.accountTypeId.toString()
                    };
                    AppInsightsLogger.trackEvent({ name: eventName }, props);
                    GoogleAnalyticsLogger.trackEvent("Profile", eventName);
                } else {
                    const eventName = "Profile: Bank account save failed";
                    AppInsightsLogger.trackEvent({ name: eventName });
                    GoogleAnalyticsLogger.trackEvent("Profile", eventName);
                }
                break;
            }
            case "REMOVE_BANK_ACCOUNT_COMPLETE": {
                if (action.succeeded) {
                    const eventName = "Profile: Bank account removed";
                    const props = {
                        bankAccountType: action.bankAccountRemoval.bankAccountTypeId.toString()
                    };
                    AppInsightsLogger.trackEvent({ name: eventName }, props);
                    GoogleAnalyticsLogger.trackEvent("Profile", eventName);
                } else {
                    const eventName = "Profile: Bank account remove failed";
                    AppInsightsLogger.trackEvent({ name: eventName });
                    GoogleAnalyticsLogger.trackEvent("Profile", eventName);
                }
                break;
            }
            case "SAVE_PHONE_NUMBERS_COMPLETE": {
                if (action.succeeded) {
                    const eventName = "Profile: Phone number saved";
                    AppInsightsLogger.trackEvent({ name: eventName });
                    GoogleAnalyticsLogger.trackEvent("Profile", eventName);
                } else {
                    const eventName = "Profile: Phone number save failed";
                    AppInsightsLogger.trackEvent({ name: eventName });
                    GoogleAnalyticsLogger.trackEvent("Profile", eventName);
                }
                break;
            }
            case "VERIFY_COMPLETE": {
                if (action.succeeded) {
                    const eventName = "Profile: Email verified";
                    AppInsightsLogger.trackEvent({ name: eventName });
                    GoogleAnalyticsLogger.trackEvent("Profile", eventName);
                } else {
                    const eventName = "Profile: Email verification failed";
                    const props = {
                        verificationError: action.verificationError || ""
                    };
                    AppInsightsLogger.trackEvent({ name: eventName }, props);
                    GoogleAnalyticsLogger.trackEvent("Profile", eventName);
                }
                break;
            }
            case "SEARCH_DRUGS_COMPLETE": {
                if (action.succeeded && action.matches.length === 0) {
                    const eventName = "Drugs: search returned no results";
                    const props = {
                        drugName: action.name
                    };
                    AppInsightsLogger.trackEvent({ name: eventName }, props);
                    GoogleAnalyticsLogger.trackEvent("Drugs", eventName);
                }
                break;
            }
            case "GET_DRUG_DETAILS_COMPLETE": {
                if (action.succeeded) {
                    const eventName = "Drugs: fetched details";
                    const props = {
                        drugName: action.name
                    };
                    AppInsightsLogger.trackEvent({ name: eventName }, props);
                    GoogleAnalyticsLogger.trackEvent("Drugs", eventName);
                }
                // Feature usage Logging
                const eventName = "FeatureUsage-DrugInquiry-DrugOpened";
                AppInsightsLogger.trackEvent(
                    { name: eventName },
                    { name: eventName, succeeded: action.succeeded, drugName: action.name }
                );
                break;
            }
            case "TRACK_DOCUMENT_DOWNLOAD": {
                const eventName = "FeatureUsage-Documents-Downloaded";
                const props = {
                    collectionName: action.collectionName
                };
                AppInsightsLogger.trackEvent({ name: eventName }, props);
                GoogleAnalyticsLogger.trackEvent(eventName, eventName);
                break;
            }
            case "TRACK_MOBILE_NAV_DRAWER_OPENED": {
                const eventName = "Mobile navigation expanded";

                const props = {
                    panelName: action.panelName
                };

                AppInsightsLogger.trackEvent({ name: eventName }, props);
                GoogleAnalyticsLogger.trackEvent(eventName, eventName);
                break;
            }
            case "TRACK_LOGGED_IN_USER_EMULATED": {
                const eventName = action.isEmulated ? "User - Emulated" : "User - Not Emulated";
                AppInsightsLogger.trackEvent({ name: eventName });
                GoogleAnalyticsLogger.trackEvent(eventName, eventName);
                break;
            }
            case "SUBMIT_INQUIRY_COMPLETE": {
                if (action.succeeded) {
                    const eventName = "Inquiry: submitted";
                    const props = {
                        subject: action.inquiry.subject,
                        preferredContactMethod: action.inquiry.preferredContactMethod
                    };
                    AppInsightsLogger.trackEvent({ name: eventName }, props);
                    GoogleAnalyticsLogger.trackEvent("Inquiry", eventName);
                }
                break;
            }
            case "SUBMIT_FEEDBACK_COMPLETE": {
                if (action.succeeded) {
                    const eventName = "Feedback: submitted";
                    const props = {
                        rating: (action.feedback ? action.feedback.rating : 0).toString()
                    };
                    AppInsightsLogger.trackEvent({ name: eventName }, props);
                    GoogleAnalyticsLogger.trackEvent("Feedback", eventName);
                }
                break;
            }
            case "SET_SELECTED_PROVIDER_CLASS": {
                if (action.providerClassId) {
                    const eventName = "Providers: filtered to class";
                    const props = {
                        classId: action.providerClassId.toString()
                    };
                    AppInsightsLogger.trackEvent({ name: eventName }, props);
                    GoogleAnalyticsLogger.trackEvent("Providers", eventName);
                }
                break;
            }
            case "GET_PROVIDERS_COMPLETE": {
                if (action.succeeded && action.page) {
                    const eventName = "Providers: fetched a page";
                    const props = {
                        numberOfServiceProvidersLoaded: action.page.items.length.toString(),
                        total: action.page.totalItemCount.toString()
                    };
                    AppInsightsLogger.trackEvent({ name: eventName }, props);
                    GoogleAnalyticsLogger.trackEvent("Providers", eventName);
                }

                // Feature usage Logging
                const eventName = "FeatureUsage-Providers-AreaSearched";
                AppInsightsLogger.trackEvent({ name: eventName }, { name: eventName, succeeded: action.succeeded });
                break;
            }
            case "SUBMIT_SERVICE_PROVIDER_RATING_COMPLETE": {
                // Feature usage Logging
                const eventName = "FeatureUsage-Providers-RatedFromMyAsebp";
                AppInsightsLogger.trackEvent(
                    { name: eventName },
                    {
                        name: eventName,
                        succeeded: action.succeeded
                    }
                );
                break;
            }
            case "SAVE_SERVICE_PROVIDER_RATING_RESPONSE_REQUEST_COMPLETE": {
                // Feature usage Logging
                const eventName = "FeatureUsage-Providers-RatedFromEmail";
                AppInsightsLogger.trackEvent(
                    { name: eventName },
                    {
                        name: eventName,
                        succeeded: action.succeeded
                    }
                );
                break;
            }
            case "GET_INELIGIBLE_PROVIDERS_COMPLETE": {
                // Feature usage Logging
                const eventName = "FeatureUsage-Providers-IneligiblePageViewed";
                AppInsightsLogger.trackEvent({ name: eventName }, { name: eventName, succeeded: action.succeeded });
                GoogleAnalyticsLogger.trackEvent("Providers", eventName);
                break;
            }
            case "GET_HOME_ADDRESS_GEO_COORDINATE_COMPLETE": {
                if (action.succeeded && action.homeAddressGeoCoordinate) {
                    const eventName = "Providers: home address geo location found";
                    AppInsightsLogger.trackEvent({ name: eventName });
                    GoogleAnalyticsLogger.trackEvent("Providers", eventName);
                }
                break;
            }

            case "LOG_DEEP_LINK": {
                const eventName = "Deeplink Clicked";
                const props = {
                    isMobile: action.isMobile,
                    verificationType: action.verificationType
                };
                AppInsightsLogger.trackEvent({ name: eventName }, props);
                GoogleAnalyticsLogger.trackEvent("Verification", eventName);
                break;
            }
            case "SAVE_PREFERENCE_COMPLETE": {
                if (action.succeeded) {
                    const eventName = "Preference: T4A preference saved";
                    AppInsightsLogger.trackEvent({ name: eventName });
                    GoogleAnalyticsLogger.trackEvent("Preference", eventName);
                } else {
                    const eventName = "Preference: T4A preference save failed";
                    AppInsightsLogger.trackEvent({ name: eventName });
                    GoogleAnalyticsLogger.trackEvent("Preference", eventName);
                }
                break;
            }
            case "GET_DENTAL_PROCEDURE_DETAILS_COMPLETE_ACTION": {
                // Feature usage Logging
                const eventName = "FeatureUsage-DentalGuide-ProcedureViewed";
                AppInsightsLogger.trackEvent(
                    { name: eventName },
                    {
                        name: eventName,
                        succeeded: action.succeeded,
                        procedureCode: action?.dentalProcedureDetails?.productExternalId
                    }
                );
                break;
            }
            default: {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const exhaustiveCheck: never = action;
            }
        }

        return next(action);
    };
}

const middleware = createLoggingMiddleware();
export default middleware;
